import { Link } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { useBaseline } from "@/hooks/use-baseline";
import { useSolution } from "@/hooks/use-solutions";

interface BaselineGateBannerProps {
  solutionId: string;
}

/**
 * Renders a warning banner when Process AI is gated off — either because
 * the solution has no baseline rows yet, or because the baseline exists
 * but has not been confirmed. Returns null when the baseline is confirmed.
 */
export function BaselineGateBanner({ solutionId }: BaselineGateBannerProps) {
  const solution = useSolution(solutionId);
  const baseline = useBaseline(solutionId);

  // Wait for both queries to load before deciding (avoid flicker).
  if (solution.isLoading || baseline.isLoading) return null;
  if (solution.data?.baselineConfirmedAt) return null;

  const hasRows = (baseline.data?.length ?? 0) > 0;
  const message = hasRows
    ? "Review and confirm your baseline to enable Process AI."
    : "Upload a baseline CSV to enable Process AI.";

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/50 px-4 py-3 flex items-start gap-3">
      <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
      <div className="flex-1 text-sm text-amber-900 dark:text-amber-100">
        <span className="font-medium">Baseline not confirmed.</span> {message}{" "}
        <Link
          to="/s/$solutionId/scope"
          params={{ solutionId }}
          search={{ view: "list" }}
          className="font-medium underline underline-offset-2 hover:text-amber-950 dark:hover:text-amber-50"
        >
          Open Scope →
        </Link>
      </div>
    </div>
  );
}
